import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GlobalModal } from "../../../global/GlobalModal";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import { GlobalButton, GlobalTextField } from "../../../global/forms/GlobalFormFields";
import {
  getVolunteerMembershipsList,
  getVolunteerRenewalsList,
  submitMembershipRenewal,
} from "../api/membershipVolunteerApi";
import {
  clearMembershipRenewFormDraft,
  clearOnlinePaymentSession,
  clearPhonePePaymentResult,
  getMembershipRenewFormDraft,
  getPhonePePaymentResult,
  isActivePhonePePayment,
  markPhonePeOrderConsumed,
  setMembershipRenewFormDraft,
} from "../../payment/paymentSession";
import { getCsrfToken } from "../../../public/api/authApi";
import MembershipRenewHistorySection from "../components/renew/sections/MembershipRenewHistorySection";
import MembershipRenewPaymentSection from "../components/renew/sections/MembershipRenewPaymentSection";
import MembershipRenewListFiltersSection from "../components/renew/sections/MembershipRenewListFiltersSection";
import MembershipRenewListDirectorySection from "../components/renew/sections/MembershipRenewListDirectorySection";
import { useMembershipRole } from "../utils/useMembershipRole";
import { MEMBERSHIPS_RENEWALS_SUBMODULE_SLUG } from "../utils/membershipPermissionSlugs";
import {
  PAGE_SIZE,
  RENEWAL_LIST_COLUMN_DEFS,
  TRANSACTION_ID_MAX_LENGTH,
  addDays,
  calcRenewalAmount,
  createInitialRenewalListColumnVisibility,
  defaultMembershipRenewListFilters,
  formatDateCell,
  formatDateOnly,
  formatMoney,
  renewalDirectoryCellDisplay,
  renewalListSortValue,
} from "../components/renew/membershipRenewCommon";
import "../styles/VolunteerTable.css";
import "../styles/membershipList.css";

export default function MembershipRenewPage() {
  const perms = useMembershipRole(MEMBERSHIPS_RENEWALS_SUBMODULE_SLUG);
  const [rows, setRows] = useState([]);
  const filterMetaRef = useRef({ memberships: [], statuses: [] });
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [sortColumn, setSortColumn] = useState("submitted_at");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [draftFilters, setDraftFilters] = useState(defaultMembershipRenewListFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultMembershipRenewListFilters);

  const paymentSectionRef = useRef(null);
  const historySectionRef = useRef(null);
  const [columnVisibility, setColumnVisibility] = useState(createInitialRenewalListColumnVisibility);

  const [selectedRenewal, setSelectedRenewal] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [phonePeCompleted, setPhonePeCompleted] = useState(() => {
    const phonePe = getPhonePePaymentResult();
    return isActivePhonePePayment(phonePe) ? phonePe : null;
  });
  const [phonePeError, setPhonePeError] = useState("");
  const [historySearch, setHistorySearch] = useState("");
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [form, setForm] = useState({
    payment_type: "offline",
    payment_transaction_id: "",
    amount: "",
    proof: "",
    proof_file_name: "",
    remarks: "",
  });

  useEffect(() => {
    const t = setTimeout(() => setAppliedFilters({ ...draftFilters }), 350);
    return () => clearTimeout(t);
  }, [draftFilters]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const queryFilters = {
        student_search: appliedFilters.student_search,
        sort_by: sortColumn,
        order: sortOrder,
        page,
        limit: PAGE_SIZE,
        ended_on_or_before_today: "1",
      };
      const fm = filterMetaRef.current;
      if (appliedFilters.column && appliedFilters.value) {
        if (appliedFilters.column === "membership_name") {
          const match = (fm.memberships || []).find(
            (m) => String(m.membership_name) === String(appliedFilters.value),
          );
          if (match?.id) queryFilters.membership_id = String(match.id);
        } else if (appliedFilters.column === "status_name") {
          const match = (fm.statuses || []).find(
            (s) => String(s.status_name) === String(appliedFilters.value),
          );
          if (match?.id) queryFilters.status_id = String(match.id);
        } else if (appliedFilters.column === "application_verified") {
          queryFilters.application_verified = appliedFilters.value === "Verified" ? "1" : "0";
        } else if (appliedFilters.column === "payment_verified") {
          queryFilters.payment_verified = appliedFilters.value === "Verified" ? "1" : "0";
        } else if (appliedFilters.column === "student_name" || appliedFilters.column === "student_code") {
          queryFilters.student_search = String(appliedFilters.value || "");
        }
      }
      const res = await getVolunteerMembershipsList(queryFilters);
      const list = Array.isArray(res.data) ? res.data : [];
      setRows(list);
      filterMetaRef.current = res.filters || { memberships: [], statuses: [] };
      setTotal(Number(res.meta?.total || 0));
      setTotalPages(Math.max(1, Number(res.meta?.totalPages || 1)));
      setPage(Number(res.meta?.page || 1));
    } catch (e) {
      setErrorMsg(e.message || "Could not load memberships list.");
      setRows([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, sortColumn, sortOrder, page]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    setPage(1);
  }, [appliedFilters, sortColumn, sortOrder]);

  useEffect(() => {
    const phonePe = getPhonePePaymentResult();
    const activePhonePe = isActivePhonePePayment(phonePe) ? phonePe : null;
    const draft = getMembershipRenewFormDraft();
    if (!draft && !activePhonePe) return;
    if (draft?.form) setForm((prev) => ({ ...prev, ...draft.form }));
    if (draft?.application_id && rows.length) {
      const match = rows.find(
        (r) => Number(r.application_id) === Number(draft.application_id),
      );
      if (match) setSelectedRenewal(match);
    }
    if (activePhonePe) setPhonePeCompleted(activePhonePe);
  }, [rows]);

  useEffect(() => {
    const search = String(historySearch || "").trim();
    if (!search) {
      setHistoryRows([]);
      setHistoryLoading(false);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setHistoryLoading(true);
      try {
        const res = await getVolunteerRenewalsList({
          student_search: search,
          sort_by: "submitted_at",
          order: "DESC",
        });
        setHistoryRows(Array.isArray(res.data) ? res.data : []);
      } catch {
        setHistoryRows([]);
      } finally {
        setHistoryLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [historySearch]);

  const sortedRows = useMemo(() => {
    const mult = sortOrder === "ASC" ? 1 : -1;
    const list = [...rows];
    list.sort((a, b) => {
      const va = renewalListSortValue(a, sortColumn);
      const vb = renewalListSortValue(b, sortColumn);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * mult;
      return (
        String(va).localeCompare(String(vb), undefined, {
          numeric: true,
          sensitivity: "base",
        }) * mult
      );
    });
    return list;
  }, [rows, sortColumn, sortOrder]);

  const previewRenewalStartDate = useMemo(() => formatDateOnly(new Date()), []);
  const previewRenewalEndDate = useMemo(() => {
    if (!selectedRenewal) return "";
    const durationDays = Number(selectedRenewal.membership_duration_days || 0);
    return durationDays > 0 ? addDays(previewRenewalStartDate, durationDays) : previewRenewalStartDate;
  }, [previewRenewalStartDate, selectedRenewal]);
  const hasSameDateRenewal = useMemo(() => {
    if (!selectedRenewal) return false;
    const existingStart = selectedRenewal.start_date ? String(selectedRenewal.start_date).slice(0, 10) : "";
    const existingEnd = selectedRenewal.end_date ? String(selectedRenewal.end_date).slice(0, 10) : "";
    return Boolean(
      existingStart &&
        existingEnd &&
        existingStart === previewRenewalStartDate &&
        existingEnd === previewRenewalEndDate,
    );
  }, [selectedRenewal, previewRenewalStartDate, previewRenewalEndDate]);

  const getRenewalExportData = useCallback(() => {
    const visible = RENEWAL_LIST_COLUMN_DEFS.filter((c) => columnVisibility[c.id]);
    const headers = visible.map((c) => c.label);
    const exportRows = sortedRows.map((row) => visible.map((c) => renewalDirectoryCellDisplay(c, row)));
    return { headers, rows: exportRows };
  }, [sortedRows, columnVisibility]);

  const openRenewalModal = (row) => {
    setSelectedRenewal(row);
    setShowDetailModal(true);
    setFormErrors({});
  };

  const proceedRenewal = () => {
    if (!selectedRenewal) return;
    if (hasSameDateRenewal) {
      setShowDetailModal(false);
      setErrorMsg("Renewal already exists for the same tentative start and end dates.");
      return;
    }
    setShowDetailModal(false);
    const renewalAmount = calcRenewalAmount(selectedRenewal);
    setForm((prev) => ({
      ...prev,
      amount: String(renewalAmount.toFixed(2)),
    }));
    requestAnimationFrame(() => {
      paymentSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const submitRenewalRequest = async () => {
    if (!perms.canCreate) {
      setErrorMsg("You do not have create access to submit renewals.");
      return;
    }
    if (!selectedRenewal) return;
    if (hasSameDateRenewal) {
      setErrorMsg("Renewal already exists for the same tentative start and end dates.");
      return;
    }
    const phonePe = getPhonePePaymentResult();
    const payingOnline =
      form.payment_type === "online" || isActivePhonePePayment(phonePe);
    const errors = {};
    if (!form.amount || Number(form.amount) <= 0) errors.amount = "Enter a valid amount";
    if (!payingOnline) {
      const ref = String(form.payment_transaction_id || "").trim();
      if (!ref) errors.payment_transaction_id = "Receipt number is required";
      else if (ref.length > TRANSACTION_ID_MAX_LENGTH) {
        errors.payment_transaction_id = `Receipt number must be at most ${TRANSACTION_ID_MAX_LENGTH} characters`;
      }
    } else if (!isActivePhonePePayment(phonePe)) {
      errors.phonepe = "Complete online payment before submitting";
    }
    if (!String(form.proof || "").trim()) {
      errors.proof = "Upload payment proof before submitting";
    }
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }
    setSubmitting(true);
    setErrorMsg("");
    try {
      let csrf = localStorage.getItem("csrf-token");
      if (!csrf) {
        csrf = await getCsrfToken();
        localStorage.setItem("csrf-token", csrf);
      }
      const paymentType =
        form.payment_type === "online" || isActivePhonePePayment(phonePe)
          ? "online"
          : "offline";
      const payload = {
        application_id: Number(selectedRenewal.application_id),
        amount: Number(form.amount),
        proof: form.proof?.trim() || null,
        remarks: form.remarks?.trim() || null,
        payment: {
          payment_type: paymentType,
          transaction_id:
            paymentType === "offline" ? form.payment_transaction_id?.trim() : undefined,
          phonepe: paymentType === "online" ? phonePe : undefined,
          proof: form.proof?.trim() || null,
          remarks: form.remarks?.trim() || null,
        },
      };
      const { data, error } = await submitMembershipRenewal(payload, csrf);
      if (error) {
        setErrorMsg(error.message || "Failed to submit renewal");
        if (error.details) setFormErrors(error.details);
        return;
      }
      if (data?.success) {
        if (phonePe?.orderId) markPhonePeOrderConsumed(phonePe.orderId);
        setSuccessMsg(
          data.data?.renewal_code
            ? `Renewal submitted successfully. Renewal code: ${data.data.renewal_code}`
            : "Renewal submitted successfully",
        );
        setSelectedRenewal(null);
        setPhonePeCompleted(null);
        clearOnlinePaymentSession();
        clearMembershipRenewFormDraft();
        setForm({
          payment_type: "offline",
          payment_transaction_id: "",
          amount: "",
          proof: "",
          proof_file_name: "",
          remarks: "",
        });
        await loadList();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSortColumnChange = useCallback((id) => {
    setSortColumn(id);
    setSortOrder("ASC");
  }, []);

  const handleSortOrderToggle = useCallback(() => {
    setSortOrder((o) => (o === "ASC" ? "DESC" : "ASC"));
  }, []);

  return (
    <div className="vml-page">
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <header className="vml-hero">
        <p className="vml-hero__eyebrow">Memberships</p>
        <h1 className="vml-hero__title">Renew Membership</h1>
      </header>

      <MembershipRenewListFiltersSection
        draftFilters={draftFilters}
        onDraftFiltersChange={setDraftFilters}
        onClearColumnValueFilter={() => setDraftFilters((f) => ({ ...f, column: "", value: "" }))}
        rows={rows}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        getExportData={getRenewalExportData}
      />

      <div className="vml-renew-layout">
        <MembershipRenewListDirectorySection
          loading={loading}
          sortedRows={sortedRows}
          columnVisibility={columnVisibility}
          sortColumn={sortColumn}
          sortOrder={sortOrder}
          onSortColumnChange={handleSortColumnChange}
          onSortOrderToggle={handleSortOrderToggle}
          onRenewalClick={openRenewalModal}
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          onScrollToHistory={() =>
            historySectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
          }
        />

        <MembershipRenewPaymentSection
          sectionRef={paymentSectionRef}
          selectedRenewal={selectedRenewal}
          form={form}
          setForm={setForm}
          formErrors={formErrors}
          phonePeCompleted={phonePeCompleted}
          phonePeError={phonePeError}
          onPhonePeError={setPhonePeError}
          onBeforePayRedirect={() => {
            // Return path is captured automatically by GlobalPaymentButton.
            clearPhonePePaymentResult();
            setPhonePeCompleted(null);
            setMembershipRenewFormDraft({
              application_id: selectedRenewal?.application_id,
              form: { ...form, payment_type: "online" },
            });
          }}
          onClearOnlinePayment={() => {
            clearOnlinePaymentSession();
            clearMembershipRenewFormDraft();
            setPhonePeCompleted(null);
            setPhonePeError("");
            setFormErrors((prev) => ({ ...prev, phonepe: "" }));
          }}
          submitting={submitting || !perms.canCreate}
          errorMsg={errorMsg}
          onSubmit={submitRenewalRequest}
        />
      </div>

      <MembershipRenewHistorySection
        sectionRef={historySectionRef}
        historySearch={historySearch}
        onHistorySearchChange={setHistorySearch}
        historyRows={historyRows}
        historyLoading={historyLoading}
      />

      <GlobalModal
        open={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="Renewal details"
        description={selectedRenewal ? `${selectedRenewal.student_name} · ${selectedRenewal.application_code}` : ""}
      >
        {selectedRenewal ? (
          <div className="vml-edit-grid">
            <GlobalTextField label="Student" value={selectedRenewal.student_name || ""} onChange={() => {}} disabled />
            <GlobalTextField label="Student code" value={selectedRenewal.student_code || ""} onChange={() => {}} disabled />
            <GlobalTextField label="Membership" value={selectedRenewal.membership_name || ""} onChange={() => {}} disabled />
            <GlobalTextField label="Start date" value={formatDateCell(previewRenewalStartDate)} onChange={() => {}} disabled />
            <GlobalTextField label="End date" value={formatDateCell(previewRenewalEndDate)} onChange={() => {}} disabled />
            <GlobalTextField label="Tentative start" value={formatDateCell(previewRenewalStartDate)} onChange={() => {}} disabled />
            <GlobalTextField label="Tentative end" value={formatDateCell(previewRenewalEndDate)} onChange={() => {}} disabled />
            <GlobalTextField label="Amount" value={formatMoney(calcRenewalAmount(selectedRenewal))} onChange={() => {}} disabled />
            {hasSameDateRenewal ? (
              <p style={{ margin: 0, color: "#b91c1c", fontSize: "0.85rem" }}>
                Renewal already exists for these tentative dates.
              </p>
            ) : null}
            <div className="vml-edit-actions">
              <GlobalButton type="button" variant="ghost" onClick={() => setShowDetailModal(false)}>
                Close
              </GlobalButton>
              <GlobalButton type="button" variant="primary" onClick={proceedRenewal} disabled={hasSameDateRenewal}>
                Proceed Renewal
              </GlobalButton>
            </div>
          </div>
        ) : null}
      </GlobalModal>
    </div>
  );
}
